import type { WorkspaceSetupProgress } from "@crewledger/shared/workspace";
import { PageHeader, InfoBanner } from "@/components/design";
import { Button } from "@/components/ui/button";
import { MiniCommandCenter } from "@/features/beta-onboarding/components/mini-command-center";
import {
  SetupChecklistGrid,
  SetupNextStepBanner,
  SetupProgressBar,
} from "@/features/beta-onboarding/components/setup-checklist-ui";
import { useDashboard } from "@/features/dashboard/hooks/use-dashboard";

interface BetaPartialDashboardPageProps {
  setup: WorkspaceSetupProgress;
  onRefreshSetup?: () => void;
  setupRefreshing?: boolean;
}

/** Beta workspace with real data started — guided checklist plus live mini command center. */
export function BetaPartialDashboardPage({
  setup,
  onRefreshSetup,
  setupRefreshing = false,
}: BetaPartialDashboardPageProps) {
  const { data, loading, refresh } = useDashboard();

  return (
    <div className="space-y-8">
      <PageHeader
        title="Welcome to Werkio"
        description="Your workspace is ready. Complete these steps to start tracking your first pay period."
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={setupRefreshing}
            onClick={() => {
              onRefreshSetup?.();
              void refresh();
            }}
          >
            Refresh
          </Button>
        }
      />

      <InfoBanner variant="info">
        This is a real Werkio workspace, not demo data. Finish setup while your live snapshot
        updates below.
      </InfoBanner>

      <SetupProgressBar
        completedCount={setup.completedCount}
        totalSteps={setup.totalSteps}
        progressPercent={setup.progressPercent}
      />

      <SetupNextStepBanner setup={setup} />

      <div className="grid gap-8 xl:grid-cols-5">
        <div className="space-y-4 xl:col-span-3">
          <h2 className="text-lg font-semibold text-navy-deep">Setup checklist</h2>
          <SetupChecklistGrid setup={setup} compact />
        </div>

        <div className="xl:col-span-2">
          <MiniCommandCenter data={data} loading={loading} />
        </div>
      </div>
    </div>
  );
}
